import { ChildConditionType } from '@invenco/common-domain/enums';
import {
  ActionDTO,
  ConditionDTO,
  PropertyConditionDTO,
  RuleDTO,
} from '@invenco/common-interface/automation';

import {
  ConfiguredEditorAction,
  EditorConditionGroup,
  EditorConditionLeaf,
  EditorConditionNode,
  EditorRule,
  isConfiguredAction,
} from './editor-model';
import { getActionDefinition, hasActionDefinition } from './field-registry';
import { actionDefinitions } from './field-registry/action-definitions';

/**
 * Deeper-than-editable nesting is preserved faithfully and flagged `tooDeep`
 * rather than coerced; the backend evaluator accepts mixed leaf/group children
 * at any depth.
 */

const isPropertyConditionDTO = (
  condition: ConditionDTO | PropertyConditionDTO,
): condition is PropertyConditionDTO => 'property' in condition;

type MintId = () => string;

const createMint = (): MintId => {
  let next = 0;
  return () => `cond-${++next}`;
};

const toEditorLeaf = (condition: PropertyConditionDTO, mint: MintId): EditorConditionLeaf => ({
  kind: 'leaf',
  id: mint(),
  property: condition.property,
  operator: condition.operator,
  value: condition.value,
  ...(condition.subProperty !== undefined && { subProperty: condition.subProperty }),
});

const toEditorGroup = (
  condition: ConditionDTO,
  mint: MintId,
  depth: number,
): EditorConditionGroup => ({
  kind: 'group',
  id: mint(),
  connective:
    condition.childConditionType ?? (depth === 1 ? ChildConditionType.ALL : ChildConditionType.ANY),
  children: (condition.childConditions ?? []).map((child): EditorConditionNode =>
    isPropertyConditionDTO(child)
      ? toEditorLeaf(child, mint)
      : toEditorGroup(child, mint, depth + 1),
  ),
});

const hasGroupBelowEditableDepth = (condition: ConditionDTO, depth: number): boolean =>
  (condition.childConditions ?? []).some(
    (child) =>
      !isPropertyConditionDTO(child) &&
      (depth >= 2 || hasGroupBelowEditableDepth(child, depth + 1)),
  );

const emptyRootGroup = (mint: MintId): EditorConditionGroup => ({
  kind: 'group',
  id: mint(),
  connective: ChildConditionType.ALL,
  children: [],
});

const normalizeRule = (rule: RuleDTO, mint: MintId): EditorRule => {
  const { condition } = rule;

  let root: EditorConditionGroup;
  let tooDeep = false;
  if (!condition) {
    root = emptyRootGroup(mint);
  } else if (isPropertyConditionDTO(condition)) {
    root = { ...emptyRootGroup(mint), children: [toEditorLeaf(condition, mint)] };
  } else {
    root = toEditorGroup(condition, mint, 1);
    tooDeep = hasGroupBelowEditableDepth(condition, 1);
  }

  return {
    name: rule.name ?? '',
    condition: root,
    tooDeep,
    placeholder: false,
    actions: rule.actions ?? [],
    elseActions: rule.elseActions ?? [],
  };
};

/** The rule materialized for an empty workflow — dropped again on denormalize while still empty. */
const defaultEditorRule = (mint: MintId): EditorRule => ({
  name: '',
  condition: emptyRootGroup(mint),
  tooDeep: false,
  placeholder: true,
  actions: [],
  elseActions: [],
});

/**
 * Builds the editor render model from stored rules. An empty workflow
 * materializes one default rule so the canvas always has a tree to render.
 */
export const normalizeRules = (rules: RuleDTO[]): EditorRule[] => {
  const mint = createMint();
  if (rules.length === 0) {
    return [defaultEditorRule(mint)];
  }
  return rules.map((rule) => normalizeRule(rule, mint));
};

const toPropertyConditionDTO = (leaf: EditorConditionLeaf): PropertyConditionDTO => ({
  property: leaf.property,
  operator: leaf.operator,
  value: leaf.value as PropertyConditionDTO['value'],
  ...(leaf.subProperty !== undefined && { subProperty: leaf.subProperty }),
});

const toConditionDTO = (group: EditorConditionGroup): ConditionDTO => ({
  childConditionType: group.connective,
  childConditions: group.children.map((child) =>
    child.kind === 'leaf' ? toPropertyConditionDTO(child) : toConditionDTO(child),
  ),
});

/** No conditions and no configured actions; a name or typeless scaffolding alone doesn't count. */
export const isRuleEmpty = (rule: EditorRule): boolean =>
  rule.condition.children.length === 0 &&
  !rule.actions.some(isConfiguredAction) &&
  !rule.elseActions.some(isConfiguredAction);

const isEmptyPlaceholder = (rule: EditorRule): boolean => rule.placeholder && isRuleEmpty(rule);

/**
 * Editing shares one param bag across type switches. Drop keys declared for a
 * different type, but preserve registry-unknown keys so deploy-skew data can
 * round-trip. Unknown action types pass through untouched for the same reason.
 */
const registryParamKeys = new Set(
  Object.values(actionDefinitions).flatMap((definition) =>
    definition.params.map((param) => param.key),
  ),
);

const toActionDTO = ({ fresh: _fresh, ...action }: ConfiguredEditorAction): ActionDTO => {
  if (!hasActionDefinition(action.type)) {
    return action;
  }
  const declared = new Set(getActionDefinition(action.type).params.map((param) => param.key));
  return {
    ...action,
    params: Object.fromEntries(
      Object.entries(action.params ?? {}).filter(
        ([key]) => declared.has(key) || !registryParamKeys.has(key),
      ),
    ),
  };
};

/**
 * Inverse of {@link normalizeRules}: strips editor-only fields and drops the
 * still-empty placeholder, so an untouched empty workflow round-trips to `[]`.
 */
export const denormalizeRules = (rules: EditorRule[]): RuleDTO[] =>
  rules
    .filter((rule) => !isEmptyPlaceholder(rule))
    .map((rule) => ({
      name: rule.name,
      condition: toConditionDTO(rule.condition),
      actions: rule.actions.filter(isConfiguredAction).map(toActionDTO),
      elseActions: rule.elseActions.filter(isConfiguredAction).map(toActionDTO),
    }));
